<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import PlanungsgeschaeftEditStammdatenPanel from '@/components/PlanungsgeschaeftEditStammdatenPanel.vue';
import { AccessControl, AccessControlCollection, AC_CREATION_LEVEL } from '@/models/AccessControl';
import AccessControlItem from '@/components/AccessControlItem.vue';
import AccessControlForm from '@/components/AccessControlForm.vue';
import { onMounted, ref, type Ref } from 'vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import { useAuthUser } from '@/helpers/globals';

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const loading: Ref<boolean> = ref(false);
const showDialog: Ref<boolean> = ref(false);
const showDeleteConfirm: Ref<boolean> = ref(false);
const acs: Ref<AccessControl[]> = ref([]);
const deleteAc: Ref<AccessControl | null> = ref(null);

onMounted(() => {
    loadRecords();
});

function onCreated(): void {
    showDialog.value = false;
    loadRecords();
}

function initializeDeleteAccess(item: AccessControl): void {
    deleteAc.value = item;
    showDeleteConfirm.value = true;
}

async function deleteAccess(): Promise<void> {
    showDeleteConfirm.value = false;
    await deleteAc.value?.destroy();
    deleteAc.value = null;
    loadRecords();
}

async function onModeChanged(mode: string, ac: AccessControl): Promise<void> {
    ac.mode = mode;
    await ac.save();
    loadRecords();
}

async function loadRecords() {
    loading.value = true;
    const acCollection = new AccessControlCollection();
    acs.value = (
        await acCollection
            .setQueryParams({
                filter: [
                    {
                        property: 'planning_permission_application_id',
                        value: props.geschaeft.id
                    },
                    {
                        property: 'creation_level',
                        value: AC_CREATION_LEVEL.CREATION_LEVEL_GEMEINDE
                    }
                ],
                sort: [{ property: 'created', direction: 'asc' }]
            })
            .query()
    ).getModels();

    loading.value = false;
}
</script>

<template>
    <div class="cta-grid">
        <div class="left flex-grow-1 ktg-form-area-maxwidth">
            <h4 class="ktg-title-04 ktg-inner-box-padding mb-2rem">{{ $t('berechtigungen') }}</h4>
            <h5 class="ktg-title-05 ktg-inner-box-padding mb-2rem">{{ $t('alle_berechtigungen') }}</h5>
            <div>
                <AccessControlItem
                    v-for="ac in acs"
                    :key="ac.id!"
                    :record="ac"
                    :geschaeft="props.geschaeft"
                    class="mb-0_5rem"
                    @deleted="initializeDeleteAccess"
                    @modechanged="onModeChanged"
                ></AccessControlItem>
            </div>
            <div
                v-if="
                    showDialog === false &&
                    (useAuthUser()?.id === props.geschaeft?.creator_id || useAuthUser()?.isRoleAdmin)
                "
                class="w-full flex flex-row justify-content-end pt-2rem"
            >
                <ktg-button size="small" trailing-icon="plus" appearance="secondary" @click="showDialog = true">{{
                    $t('add_additional_access')
                }}</ktg-button>
            </div>
            <div v-if="showDialog" class="mt-2_5rem">
                <h5 class="ktg-title-05 ktg-inner-box-padding mb-2rem">{{ $t('weitere_berechtigte') }}</h5>
                <AccessControlForm
                    :geschaeft="props.geschaeft"
                    @canceled="showDialog = false"
                    @created="onCreated"
                ></AccessControlForm>
            </div>
        </div>
        <div class="right">
            <h6 class="ktg-title-06 mb-1_5rem">{{ $t('stammdaten') }}</h6>
            <PlanungsgeschaeftEditStammdatenPanel class="ktg-box-margin" :geschaeft="props.geschaeft"></PlanungsgeschaeftEditStammdatenPanel>
        </div>
        <ConfirmModal
            v-model="showDeleteConfirm"
            :message="$t('confirm_destroy_access')"
            @cancel="showDeleteConfirm = false"
            @confirm="deleteAccess"
        ></ConfirmModal>
    </div>
</template>

<style lang="scss" scoped>
.right {
    width: 560px;
    flex-basis: 560px;
}
</style>
